import { useState } from "react";
import { Plus, Trash2, ArrowUp, ArrowDown, Film, EyeOff, Eye } from "lucide-react";
import { Input, Area, Select, MultiSelect, ImageInput, VideoInput, GalleryField, ActionsEditor, DEFAULT_GALLERY_SETTINGS } from "./fields";
import { FrameEditor } from "./FrameEditor";
import { FicheOverlay, ItemCard, CardGrid } from "./Fiche";
import { FORMATS, GENRES, THEMATIQUES, ROLES, PARTENAIRES_DEFAUT, YEARS } from "@/data/realisationTaxonomy";

const uid = () => `proj-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
const cuid = () => `cat-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 5)}`;
const slugify = (s = "") => s.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const STATUS = [{ value: "published", label: "Publié" }, { value: "hidden", label: "Masqué" }];
const MEDIA = [{ value: "image", label: "Image" }, { value: "video", label: "Vidéo" }];
const SIZE_OPTS = [{ value: "large", label: "Grand bloc" }, { value: "small", label: "Petit bloc" }];
const LINK_OPTS = [{ value: "detail", label: "Ouvrir la fiche détaillée (sur le site)" }, { value: "external", label: "Ouvrir un lien externe (YouTube/Vimeo…)" }];

// Normalise une valeur « multi » (chaîne libre héritée OU tableau) en tableau de chaînes.
const toList = (v) => {
  if (Array.isArray(v)) return v.filter(Boolean);
  if (typeof v === "string" && v.trim()) {
    // accepte les anciennes valeurs libres séparées par « , » ou « + »
    return v.split(/[+,]/).map((s) => s.trim()).filter(Boolean);
  }
  return [];
};

const spanToSize = (span) => (/col-span-(7|8|9|10|11|12)/.test(span || "") ? "large" : "small");
const sizeToSpan = (size) => (size === "large" ? "md:col-span-8" : "md:col-span-4");
const DEFAULT_CATS = ["Court-métrage", "Documentaire", "Mariage", "Publicité", "Événementiel"];

// ---- Categories manager (add / rename / hide / delete) ----
const CategoriesEditor = ({ items = [], onChange, usedLabels = [] }) => {
  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; onChange(c); };
  return (
    <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 space-y-3" data-testid="realisation-categories-editor">
      <div className="flex items-center justify-between">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Catégories</span>
        <button onClick={() => onChange([...items, { id: cuid(), label: "Nouvelle catégorie", hidden: false }])} data-testid="add-category-btn" className="inline-flex items-center gap-1 text-xs text-white/70 hover:text-electric"><Plus size={13} /> Ajouter</button>
      </div>
      {items.length === 0 && <p className="text-xs text-secondary2">Aucune catégorie. Ajoutez-en une.</p>}
      {items.map((c, i) => {
        const used = usedLabels.includes(c.label);
        return (
          <div key={c.id || i} className="flex items-center gap-2" data-testid={`category-row-${i}`}>
            <input value={c.label} onChange={(e) => patch(i, { label: e.target.value })}
              className="flex-1 bg-[#0e0e0e] border border-white/10 rounded px-3 py-2 text-white text-sm focus:border-electric outline-none" />
            <button onClick={() => patch(i, { hidden: !c.hidden })} title={c.hidden ? "Masquée — cliquer pour afficher" : "Visible — cliquer pour masquer"}
              className={`p-2 rounded border border-white/10 ${c.hidden ? "text-white/30" : "text-electric"} hover:border-electric`}>{c.hidden ? <EyeOff size={14} /> : <Eye size={14} />}</button>
            <button onClick={() => { if (used && !window.confirm("Cette catégorie est utilisée par une réalisation. La supprimer quand même ?")) return; onChange(items.filter((_, x) => x !== i)); }}
              className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
          </div>
        );
      })}
    </div>
  );
};

export const AdminRealisations = ({ value = [], meta = {}, onChange, onMetaChange }) => {
  const [open, setOpen] = useState(null);
  const items = value || [];
  const cats = meta.categories && meta.categories.length ? meta.categories : DEFAULT_CATS.map((l, i) => ({ id: `cat-${i}`, label: l, hidden: false }));
  const usedLabels = [...new Set(items.map((it) => it.cat).filter(Boolean))];
  const catOptions = cats.filter((c) => !c.hidden).map((c) => ({ value: c.label, label: c.label }));

  const patch = (i, p) => { const c = [...items]; c[i] = { ...c[i], ...p }; onChange(c); };
  const patchInfo = (i, k, v) => patch(i, { info: { ...(items[i].info || {}), [k]: v } });
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const c = [...items]; [c[i], c[j]] = [c[j], c[i]]; onChange(c); };
  const add = () => {
    onChange([...items, { id: uid(), slug: `projet-${items.length + 1}`, title: "Nouveau projet", cat: catOptions[0]?.value || "Court-métrage", span: "md:col-span-8", mediaType: "image", img: "", videoUrl: "", gallery: [], gallerySettings: { ...DEFAULT_GALLERY_SETTINGS }, synopsis: "", description: "", filmUrl: "", makingOf: "", linkType: "detail", externalUrl: "", info: {}, status: "published", order: items.length }]);
    setOpen(items.length);
  };
  const setMeta = (k, v) => onMetaChange({ ...meta, [k]: v });

  // Fiche dédiée : formulaire complet d'UNE réalisation (modification isolée)
  const renderForm = (i) => {
    const it = items[i];
    if (!it) return null;
    const size = spanToSize(it.span);
    const catList = catOptions.some((o) => o.value === it.cat) ? catOptions : [...catOptions, { value: it.cat, label: `${it.cat} (ancienne)` }];
    const info = it.info || {};
    // Suggestions partenaires = liste centrale (site) + prédéfinis, sans doublons.
    const partnerSuggestions = [...new Set([...(meta.partnersList || []), ...PARTENAIRES_DEFAUT])];
    const addPartnerToCentral = (name) => {
      const list = meta.partnersList || [];
      if (!list.some((p) => p.toLowerCase() === name.toLowerCase())) {
        setMeta("partnersList", [...list, name]);
      }
    };
    return (
      <>
        <a href={`/projet/${it.slug}`} target="_blank" rel="noreferrer" className="inline-block text-[11px] text-secondary2 hover:text-electric">/projet/{it.slug}</a>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Titre" value={it.title} onChange={(v) => patch(i, { title: v, slug: it._autoslug !== false ? slugify(v) : it.slug })} />
          <Input label="Slug (URL)" value={it.slug} onChange={(v) => patch(i, { slug: slugify(v), _autoslug: false })} />
          <Select label="Catégorie" value={it.cat} onChange={(v) => patch(i, { cat: v })} options={catList} />
          <Select label="Année" value={info.year} onChange={(v) => patchInfo(i, "year", v)} testid={`realisation-year-${i}`}
            options={[{ value: "", label: "— Non renseignée —" }, ...YEARS.map((y) => ({ value: y, label: y }))]} />
          <Select label="Taille du bloc (accueil)" value={size} onChange={(v) => patch(i, { span: sizeToSpan(v) })} options={SIZE_OPTS} />
          <Select label="Visibilité" value={it.status} onChange={(v) => patch(i, { status: v })} options={STATUS} />
        </div>

        <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2 inline-flex items-center gap-2"><Film size={12} /> Média d'en-tête (miniature + fiche)</span>
          <ImageInput label="Image de couverture / miniature" value={it.img} onChange={(v) => patch(i, { img: v })} />
          <Select label="Type de média d'en-tête" value={it.mediaType || "image"} onChange={(v) => patch(i, { mediaType: v })} options={MEDIA} />
          {it.mediaType === "video" && <VideoInput label="Vidéo (MP4 importé ou URL YouTube/Vimeo)" value={it.videoUrl} onChange={(v) => patch(i, { videoUrl: v })} />}
          <FrameEditor label="Cadrage — miniature (carte accueil)" src={it.img} value={it.imgFrame} onChange={(v) => patch(i, { imgFrame: v })} aspect="video" testid={`realisation-frame-card-${i}`} hint="Recadrez la vignette telle qu'elle apparaît sur l'accueil et les listes." />
          {(it.mediaType || "image") === "image" && (
            <FrameEditor label="Cadrage — grande affiche (fiche du projet)" src={it.img} value={it.detailFrame} onChange={(v) => patch(i, { detailFrame: v })} aspect="video" testid={`realisation-frame-detail-${i}`} hint="Cadrage indépendant pour la grande image en haut de la fiche." />
          )}
        </div>

        <div className="rounded border border-white/10 bg-[#050505] p-4 space-y-3">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Comportement au clic (sur l'accueil)</span>
          <Select label="Quand on clique sur ce bloc" value={it.linkType || "detail"} onChange={(v) => patch(i, { linkType: v })} options={LINK_OPTS} />
          {it.linkType === "external" && <Input label="Lien externe (YouTube, Vimeo…)" value={it.externalUrl} onChange={(v) => patch(i, { externalUrl: v })} placeholder="https://youtube.com/watch?v=…" />}
        </div>

        <Area label="Synopsis (résumé court)" value={it.synopsis} onChange={(v) => patch(i, { synopsis: v })} aiContext="le synopsis court d'un projet audiovisuel" />
        <Area label="Description complète / synopsis long (optionnel)" value={it.description} onChange={(v) => patch(i, { description: v })} rows={5} aiContext="la description complète d'un projet audiovisuel" />
        <div className="pt-4 border-t border-white/10"><ActionsEditor items={it.actions} onChange={(v) => patch(i, { actions: v })} /></div>

        <GalleryField label="Galerie de médias (photos & vidéos — fiche)" items={it.gallery || []} settings={it.gallerySettings}
          onItemsChange={(v) => patch(i, { gallery: v })} onSettingsChange={(v) => patch(i, { gallerySettings: v })} testid={`realisation-gallery-${i}`} />

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Input label="Lien du film / bande-annonce (optionnel)" value={it.filmUrl} onChange={(v) => patch(i, { filmUrl: v })} placeholder="https://youtube.com/…" />
          <Input label="Making-of (lien vidéo, optionnel)" value={it.makingOf} onChange={(v) => patch(i, { makingOf: v })} placeholder="https://…" />
        </div>

        <div className="border-t border-white/10 pt-4 space-y-4">
          <span className="text-[10px] tracking-[0.2em] uppercase text-secondary2">Fiche technique — sélection rapide (choix multiples possibles, « Autre » pour saisie libre)</span>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
            <MultiSelect label="Format de réalisation" value={toList(info.format)} suggestions={FORMATS}
              onChange={(v) => patchInfo(i, "format", v)} testid={`realisation-format-${i}`} />
            <MultiSelect label="Genre(s) cinématographique(s)" value={toList(info.genre)} suggestions={GENRES}
              onChange={(v) => patchInfo(i, "genre", v)} testid={`realisation-genre-${i}`} />
            <MultiSelect label="Thématique(s)" value={toList(info.thematique)} suggestions={THEMATIQUES}
              onChange={(v) => patchInfo(i, "thematique", v)} testid={`realisation-thematique-${i}`} />
            <MultiSelect label="Notre rôle dans le projet" value={toList(info.role)} suggestions={ROLES}
              onChange={(v) => patchInfo(i, "role", v)} testid={`realisation-role-${i}`} />
            <MultiSelect label="Partenaires & collaborateurs" value={toList(info.partners)} suggestions={partnerSuggestions}
              onChange={(v) => patchInfo(i, "partners", v)} onCustomAdd={addPartnerToCentral} testid={`realisation-partners-${i}`} />
            <div className="grid grid-cols-2 gap-3">
              <Input label="Lieu" value={info.location} onChange={(v) => patchInfo(i, "location", v)} />
              <Input label="Durée" value={info.duration} onChange={(v) => patchInfo(i, "duration", v)} placeholder="ex : 12 min" />
            </div>
          </div>
        </div>
      </>
    );
  };

  return (
    <div className="space-y-6" data-testid="admin-realisations-editor">
      <div className="rounded-lg border border-electric/20 bg-[#0a0a0a] p-4 text-sm text-secondary2">
        Portfolio des travaux terminés. Chaque réalisation peut ouvrir une <strong className="text-white">fiche détaillée</strong> ou un <strong className="text-white">lien externe</strong> (YouTube/Vimeo). La mosaïque se réorganise automatiquement.
      </div>

      <Input label="Sur-titre de section" value={meta.eyebrow} onChange={(v) => setMeta("eyebrow", v)} />

      <CategoriesEditor items={cats} usedLabels={usedLabels} onChange={(v) => setMeta("categories", v)} />

      <div className="flex items-center justify-between border-t border-white/10 pt-5">
        <span className="text-[11px] tracking-[0.25em] uppercase text-electric">Projets & réalisations ({items.length})</span>
        <button onClick={add} data-testid="admin-add-realisation" className="inline-flex items-center gap-2 text-xs text-white/70 hover:text-electric"><Plus size={14} /> Ajouter un projet</button>
      </div>

      {open !== null && items[open] ? (
        <FicheOverlay testid="realisation-fiche" title={items[open].title} subtitle={items[open].cat || "Réalisation"} onClose={() => setOpen(null)}>
          {renderForm(open)}
        </FicheOverlay>
      ) : (
        <CardGrid testid="realisations-cards">
          {items.map((it, i) => (
            <ItemCard key={it.id || i} testid={`admin-realisation-item-${i}`}
              image={it.img} frame={it.imgFrame}
              title={it.title} subtitle={[it.cat, (it.info || {}).year].filter(Boolean).join(" · ")}
              dimmed={it.status === "hidden"}
              badges={[
                { label: it.status === "hidden" ? "Masqué" : "Publié", tone: it.status === "hidden" ? "text-white/50 border-white/15 bg-black/60" : "text-electric border-electric/40 bg-black/60" },
                { label: spanToSize(it.span) === "large" ? "Grand bloc" : "Petit bloc", tone: "text-white/60 border-white/15 bg-black/60" },
              ]}
              onOpen={() => setOpen(i)}
              actions={
                <>
                  <button onClick={() => move(i, -1)} title="Monter" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowUp size={14} /></button>
                  <button onClick={() => move(i, 1)} title="Descendre" className="p-2 rounded border border-white/10 text-white/40 hover:text-electric"><ArrowDown size={14} /></button>
                  <button onClick={() => onChange(items.filter((_, x) => x !== i))} title="Supprimer" className="p-2 rounded border border-white/10 text-red-400/70 hover:text-red-400 hover:border-red-400/40"><Trash2 size={14} /></button>
                </>
              } />
          ))}
        </CardGrid>
      )}
    </div>
  );
};
